import React, { useState, useEffect, useRef } from "react";
import { Card, Table, Input, Button, Typography, Space, message, Modal, Tooltip, Select } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useTender } from "../../../contexts/TenderContext";
import MainLayout from "../../layout/MainLayout";
import WallsSpecificationContainer from "./WallsSpecificationContainer";
import RetScreenWallsQuants from "./RetScreenWallsQuants";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import NumberInput from "../../common/NumberInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { saveRetainingWallsBuildUp } from "../../../utils/wallsBuildUpSave";
import { useQueryClient } from "@tanstack/react-query";
import { RETAINING_WALLS_BUILD_UP_QUERY_KEYS } from "../../../hooks/useRetainingWallsBuildUp";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";
import { addColumnCodes } from "@/utils/addColumnCodes";
import {
  RETAINING_WALLS_REFERENCES,
  SCREEN_WALLS_FREE_ISSUE_LABEL,
  SCREEN_WALLS_M_J_SUPPLY_LABEL,
  EO_PILLAR_FREE_ISSUE_LABEL,
  EO_PILLAR_M_J_SUPPLY_LABEL,
  resolveScreenWallsCode,
  resolveEOPillarCode,
} from "@/utils/retainingWallsReferenceMapping";

const { Title, Text } = Typography;

const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "Housetype" },
  { key: "screenWalls", title: "Screen Walls" },
  { key: "screenWallsEO", title: "Screen Walls EO Pillar (Nr)" },
  { key: "screenWallsSof", title: "Screen Walls Sof 450mm (m)" },
  { key: "screenWallsLog", title: "Screen Walls Log 1200mm (m)" },
  { key: "screenWallsGravelBoard", title: "Screen Walls Gravel Board (m)" },
  { key: "screenWallsAdditionalExcavation", title: "Screen Walls Additional Excavation (m3)" },
  { key: "screenWallsAdditionalConcrete", title: "Screen Walls Additional Concrete (m3)" },
  { key: "grw450", title: "Garden Retaining Wall 450 (m)" },
  { key: "grw600", title: "Garden Retaining Wall 600 (m)" },
  { key: "grw750", title: "Garden Retaining Wall 750 (m)" },
  { key: "grw900", title: "Garden Retaining Wall 900 (m)" },
  { key: "grw1050", title: "Garden Retaining Wall 1050 (m)" },
  { key: "grw1200", title: "Garden Retaining Wall 1200 (m)" },
  { key: "grw1500", title: "Garden Retaining Wall 1500 (m)" },
  { key: "grw1800", title: "Garden Retaining Wall 1800 (m)" },
  { key: "grw2000", title: "Garden Retaining Wall 2000 (m)" },
  { key: "drw450", title: "Drive Retaining Wall 450 (m)" },
  { key: "drw600", title: "Drive Retaining Wall 600 (m)" },
  { key: "drw750", title: "Drive Retaining Wall 750 (m)" },
  { key: "drw900", title: "Drive Retaining Wall 900 (m)" },
  { key: "drw1050", title: "Drive Retaining Wall 1050 (m)" },
  { key: "drw1200", title: "Drive Retaining Wall 1200 (m)" },
  { key: "drw1500", title: "Drive Retaining Wall 1500 (m)" },
  { key: "drw1800", title: "Drive Retaining Wall 1800 (m)" },
  { key: "drw2000", title: "Drive Retaining Wall 2000 (m)" },
  { key: "drw2250", title: "Drive Retaining Wall 2250 (m)" },
  { key: "drw2500", title: "Drive Retaining Wall 2500 (m)" },
  { key: "drw2750", title: "Drive Retaining Wall 2750 (m)" },
  { key: "drw3000", title: "Drive Retaining Wall 3000 (m)" },
  { key: "ccrw1500", title: "Concrete Core Wall 1500 (m)" },
  { key: "ccrw1800", title: "Concrete Core Wall 1800 (m)" },
  { key: "ccrw2000", title: "Concrete Core Wall 2000 (m)" },
  { key: "ccrw2250", title: "Concrete Core Wall 2250 (m)" },
  { key: "ccrw2500", title: "Concrete Core Wall 2500 (m)" },
  { key: "ccrw2750", title: "Concrete Core Wall 2750 (m)" },
  { key: "ccrw3000", title: "Concrete Core Wall 3000 (m)" },
  { key: "ccrw3250", title: "Concrete Core Wall 3250 (m)" },
  { key: "ccrw3500", title: "Concrete Core Wall 3500 (m)" },
];

interface RetainingWallsRow {
  key: string;
  plotNo: string;
  housetype: string;

  // Screen Walls section
  screenWalls: number;
  screenWallsEO: number; // m
  screenWallsSof: number; // m
  screenWallsLog: number; // m
  screenWallsGravelBoard: number; // m
  screenWallsAdditionalExcavation: number; // m3
  screenWallsAdditionalConcrete: number; // m3

  // Garden Retaining Wall sections
  grw450: number; // m
  grw600: number; // m
  grw750: number; // m
  grw900: number; // m
  grw1050: number; // m
  grw1200: number; // m
  grw1500: number; // m
  grw1800: number; // m
  grw2000: number; // m

  // Concrete Core Walls
  ccrw1500: number; // m
  ccrw1800: number; // m
  ccrw2000: number; // m
  ccrw2250: number; // m
  ccrw2500: number; // m
  ccrw2750: number; // m
  ccrw3000: number; // m
  ccrw3250: number; // m
  ccrw3500: number; // m

  // Drive Retaining Walls
  drw450: number; // m
  drw600: number; // m
  drw750: number; // m
  drw900: number; // m
  drw1050: number; // m
  drw1200: number; // m
  drw1500: number; // m
  drw1800: number; // m
  drw2000: number; // m
  drw2250: number; // m
  drw2500: number; // m
  drw2750: number; // m
  drw3000: number; // m
}

const RetainingWalls: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();
  const queryClient = useQueryClient();

  // Format column title to display field name and unit separately
  const formatColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+\((m[²³]|m|Nr|t)\)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+\((m[²³]|m|Nr|t)\)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    []
  );

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent =
    tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState(
    "groundworks_includePreliminariesDisplay",
    preliminariesPercent,
  );
  const includeOhp = getCheckboxState(
    "groundworks_includeOhpDisplay",
    ohpPercent,
  );
  const includeDiscount = getCheckboxState(
    "groundworks_includeDiscountDisplay",
    discountPercent,
  );

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<RetainingWallsRow[]>([]);
  const [activeTab, setActiveTab] = useState<string>("quantities");
  const [tableKey, setTableKey] = useState(0);
  // Defers the Quantities table's mount by one paint cycle so a tender with
  // a lot of plots doesn't make this step look frozen while the table's
  // render/layout blocks the main thread - see useTableRenderDeferral's own
  // doc comment. Re-keyed on `activeTab` since switching tabs here
  // unmounts/mounts a brand new Table, not just a dataSource update - so the
  // loader should reappear on every tab switch, not just the initial mount.
  // Purely a perceived-lag fix: once it flips false the table renders
  // exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedRetainingWallKeys, setSelectedRetainingWallKeys] = useState<React.Key[]>([]);
  const [isQuantsModalOpen, setIsQuantsModalOpen] = useState(false);

  // Lifted controls for the Walls Section Build Up tab — the Wall Type select
  // and Configure button live on the tab line (same row as the tab buttons).
  const [buildUpSelectedWallType, setBuildUpSelectedWallType] = useState<string>("all");
  const [buildUpConfigOpen, setBuildUpConfigOpen] = useState<boolean>(false);
  const [buildUpTempFacingLabel, setBuildUpTempFacingLabel] = useState<string>(
    tender?.groundworks?.wallsBuildUpConfig?.facingBrickworkLabel ?? "Facing Brickwork – free issue",
  );
  const [buildUpWallTypeOptions, setBuildUpWallTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "All Wall Types" },
  ]);
  const buildUpFacingLabel =
    tender?.groundworks?.wallsBuildUpConfig?.facingBrickworkLabel ?? "Facing Brickwork – free issue";

  // Configure button for the "Ret & Screen Take off" tab — lets the user pick
  // Free Issue/M&J Supply independently for the Screen Walls column and the
  // EO Pillar column, which swaps the item-library evaluate codes used to
  // price each one.
  const [screenWallsConfigOpen, setScreenWallsConfigOpen] = useState<boolean>(false);
  const screenWallsLabel =
    tender?.groundworks?.wallsBuildUpConfig?.screenWallsLabel ?? SCREEN_WALLS_FREE_ISSUE_LABEL;
  const eoPillarLabel =
    tender?.groundworks?.wallsBuildUpConfig?.eoPillarLabel ?? EO_PILLAR_FREE_ISSUE_LABEL;
  const [tempScreenWallsLabel, setTempScreenWallsLabel] = useState<string>(screenWallsLabel);
  const [tempEOPillarLabel, setTempEOPillarLabel] = useState<string>(eoPillarLabel);
  const screenWallsSuffix =
    screenWallsLabel === SCREEN_WALLS_M_J_SUPPLY_LABEL ? "M&J Supply" : "Free Issue";
  const eoPillarSuffix =
    eoPillarLabel === EO_PILLAR_M_J_SUPPLY_LABEL ? "M&J Supply" : "Free Issue";

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<RetainingWallsRow[]>({
    pageId: "retaining-walls",
    onChangeDetected: (changes) => {
    },
  }); */

  // Force table re-render when switching tabs
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [activeTab]);

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<RetainingWallsRow>({
    filename: "RetainingWalls",
    sheetName: "Retaining Walls",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => ({
        ...createEmptyRow(`imported_${Date.now()}_${index}`),
        ...row,
      }));
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        retainingWallsTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];

          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No"),
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];

          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Find where actual data starts by looking for the first row with a valid house type
          let dataStartIndex = 0;
          let foundDataStart = false;

          for (let i = 0; i < allDataRows.length; i++) {
            const row = allDataRows[i];
            if (row && row.length > 0) {
              const plotNoValue = String(row[0] || "").trim();
              const houseTypeValue = String(row[1] || "").trim();

              // Look for first row that has both plot number and house type that look like actual data
              // House types should be text (not empty, not "total", not purely numeric)
              const hasValidPlotNo =
                plotNoValue &&
                plotNoValue !== "" &&
                !plotNoValue.toLowerCase().includes("total");
              const hasValidHouseType =
                houseTypeValue &&
                houseTypeValue !== "" &&
                !houseTypeValue.toLowerCase().includes("total") &&
                !houseTypeValue.toLowerCase().includes("sum") &&
                isNaN(Number(houseTypeValue)) && // Not purely numeric
                houseTypeValue.match(/[a-zA-Z]/); // Contains letters

              if (hasValidPlotNo && hasValidHouseType) {
                dataStartIndex = i;
                foundDataStart = true;
                break;
              } else {
              }
            }
          }

          if (!foundDataStart) {
          }

          const dataRows = allDataRows.slice(dataStartIndex);

          // Find column indices by matching exact header names
          const findColumnIndex = (searchTerms: string[]) => {
            return headers.findIndex((header: any) => {
              const headerStr = String(header || "")
                .toLowerCase()
                .trim();
              return searchTerms.some((term) =>
                headerStr.includes(term.toLowerCase()),
              );
            });
          };

          // Find column by exact match
          const findColumnIndexExact = (searchTerms: string[]) => {
            return headers.findIndex((header: any) => {
              const headerStr = String(header || "")
                .toLowerCase()
                .trim();
              return searchTerms.some(
                (term) => headerStr === term.toLowerCase(),
              );
            });
          };

          // Basic columns
          const plotNoIdx = findColumnIndex(["plot no", "plotno"]);
          const housetypeIdx = findColumnIndex(["housetype", "house type"]);

          // Screen Walls section - find by header content
          const screenWallsIdx = findColumnIndexExact(["screen walls"]);
          const screenWallsEOIdx = findColumnIndex([
            "eo pillar",
            "screen walls eo",
            "eo",
            "pillar",
          ]);
          const screenWallsLogIdx = findColumnIndex([
            "log 1200",
            "screen walls log",
            "log 1200mm",
            "log",
          ]);
          const screenWallsGravelBoardIdx = findColumnIndex([
            "gravel board",
            "gravel",
            "screen walls gravel",
          ]);
          const screenWallsAdditionalExcavationIdx = findColumnIndex([
            "additional excavation",
            "screen walls additional excavation",
          ]);
          const screenWallsAdditionalConcreteIdx = findColumnIndex([
            "additional concrete",
            "screen walls additional concrete",
          ]);
          const screenWallsSofIdx = findColumnIndex([
            "sof 450",
            "screen walls sof",
            "sof 450mm",
            "sof",
          ]);

          // Garden Retaining Wall section - find by header content
          const grw450Idx = findColumnIndex([
            "grw450",
            "garden retaining wall 450",
            "grw 450",
            "garden 450",
          ]);
          const grw600Idx = findColumnIndex([
            "grw600",
            "garden retaining wall 600",
            "grw 600",
            "garden 600",
          ]);
          const grw750Idx = findColumnIndex([
            "grw750",
            "garden retaining wall 750",
            "grw 750",
            "garden 750",
          ]);
          const grw900Idx = findColumnIndex([
            "grw900",
            "garden retaining wall 900",
            "grw 900",
            "garden 900",
          ]);
          const grw1050Idx = findColumnIndex([
            "grw1050",
            "garden retaining wall 1050",
            "grw 1050",
            "garden 1050",
          ]);
          const grw1200Idx = findColumnIndex([
            "grw1200",
            "garden retaining wall 1200",
            "grw 1200",
            "garden 1200",
          ]);
          const grw1500Idx = findColumnIndex([
            "grw1500",
            "garden retaining wall 1500",
            "grw 1500",
            "garden 1500",
          ]);
          const grw1800Idx = findColumnIndex([
            "grw1800",
            "garden retaining wall 1800",
            "grw 1800",
            "garden 1800",
          ]);
          const grw2000Idx = findColumnIndex([
            "grw2000",
            "garden retaining wall 2000",
            "grw 2000",
            "garden 2000",
          ]);
          // Concrete Core Walls section - find by header content
          const ccrw1500Idx = findColumnIndex(["ccrw1500", "concrete core wall 1500", "ccrw 1500"]);
          const ccrw1800Idx = findColumnIndex(["ccrw1800", "concrete core wall 1800", "ccrw 1800"]);
          const ccrw2000Idx = findColumnIndex(["ccrw2000", "concrete core wall 2000", "ccrw 2000"]);
          const ccrw2250Idx = findColumnIndex(["ccrw2250", "concrete core wall 2250", "ccrw 2250"]);
          const ccrw2500Idx = findColumnIndex([
            "ccrw2500",
            "concrete core wall 2500",
            "concrete core retaining wall 2500",
            "ccrw 2500",
          ]);
          const ccrw2750Idx = findColumnIndex([
            "ccrw2750",
            "concrete core wall 2750",
            "concrete core retaining wall 2750",
            "ccrw 2750",
          ]);
          const ccrw3000Idx = findColumnIndex([
            "ccrw3000",
            "concrete core wall 3000",
            "concrete core retaining wall 3000",
            "ccrw 3000",
          ]);
          const ccrw3250Idx = findColumnIndex(["ccrw3250", "concrete core wall 3250", "ccrw 3250"]);
          const ccrw3500Idx = findColumnIndex(["ccrw3500", "concrete core wall 3500", "ccrw 3500"]);

          // Drive Retaining Wall section - find by header content
          const drw450Idx = findColumnIndex([
            "drw450",
            "drive retaining wall 450",
            "drw 450",
            "drive 450",
          ]);
          const drw600Idx = findColumnIndex([
            "drw600",
            "drive retaining wall 600",
            "drw 600",
            "drive 600",
          ]);
          const drw750Idx = findColumnIndex([
            "drw750",
            "drive retaining wall 750",
            "drw 750",
            "drive 750",
          ]);
          const drw900Idx = findColumnIndex([
            "drw900",
            "drive retaining wall 900",
            "drw 900",
            "drive 900",
          ]);
          const drw1050Idx = findColumnIndex([
            "drw1050",
            "drive retaining wall 1050",
            "drw 1050",
            "drive 1050",
          ]);
          const drw1200Idx = findColumnIndex([
            "drw1200",
            "drive retaining wall 1200",
            "drw 1200",
            "drive 1200",
          ]);
          const drw1500Idx = findColumnIndex([
            "drw1500",
            "drive retaining wall 1500",
            "drw 1500",
            "drive 1500",
          ]);
          const drw1800Idx = findColumnIndex([
            "drw1800",
            "drive retaining wall 1800",
            "drw 1800",
            "drive 1800",
          ]);
          const drw2000Idx = findColumnIndex([
            "drw2000",
            "drive retaining wall 2000",
            "drw 2000",
            "drive 2000",
          ]);
          const drw2250Idx = findColumnIndex([
            "drw2250",
            "drive retaining wall 2250",
            "drw 2250",
            "drive 2250",
          ]);
          const drw2500Idx = findColumnIndex([
            "drw2500",
            "drive retaining wall 2500",
            "drw 2500",
            "drive 2500",
          ]);
          const drw2750Idx = findColumnIndex([
            "drw2750",
            "drive retaining wall 2750",
            "drw 2750",
            "drive 2750",
          ]);
          const drw3000Idx = findColumnIndex([
            "drw3000",
            "drive retaining wall 3000",
            "drw 3000",
            "drive 3000",
          ]);

          // Validate that essential columns are found
          if (plotNoIdx === -1) {
            alert(
              "Could not find 'Plot No' column in the Excel file. Please check the header format.",
            );
            return;
          }

          // Helper function to safely extract column values
          const getColumnValue = (
            row: any[],
            colIdx: number,
            defaultValue: any = 0,
          ) => {
            return colIdx !== -1 &&
              row[colIdx] !== undefined &&
              row[colIdx] !== null
              ? row[colIdx]
              : defaultValue;
          };

          const allImportedRows: RetainingWallsRow[] = dataRows
            .filter(
              (row) =>
                row &&
                row.length > 0 &&
                row.some(
                  (cell) => cell !== null && cell !== undefined && cell !== "",
                ),
            )
            .map((row, index) => {
              return {
                key: `imported_${Date.now()}_${index}`,
                plotNo: String(getColumnValue(row, plotNoIdx, "")),
                housetype: String(getColumnValue(row, housetypeIdx, "")),
                screenWalls: Number(getColumnValue(row, screenWallsIdx, 0)),
                screenWallsEO: Number(getColumnValue(row, screenWallsEOIdx, 0)),
                screenWallsSof: Number(
                  getColumnValue(row, screenWallsSofIdx, 0),
                ),
                screenWallsLog: Number(
                  getColumnValue(row, screenWallsLogIdx, 0),
                ),
                screenWallsGravelBoard: Number(
                  getColumnValue(row, screenWallsGravelBoardIdx, 0),
                ),
                screenWallsAdditionalExcavation: Number(
                  getColumnValue(row, screenWallsAdditionalExcavationIdx, 0),
                ),
                screenWallsAdditionalConcrete: Number(
                  getColumnValue(row, screenWallsAdditionalConcreteIdx, 0),
                ),
                grw450: Number(getColumnValue(row, grw450Idx, 0)),
                grw600: Number(getColumnValue(row, grw600Idx, 0)),
                grw750: Number(getColumnValue(row, grw750Idx, 0)),
                grw900: Number(getColumnValue(row, grw900Idx, 0)),
                grw1050: Number(getColumnValue(row, grw1050Idx, 0)),
                grw1200: Number(getColumnValue(row, grw1200Idx, 0)),
                grw1500: Number(getColumnValue(row, grw1500Idx, 0)),
                grw1800: Number(getColumnValue(row, grw1800Idx, 0)),
                grw2000: Number(getColumnValue(row, grw2000Idx, 0)),
                ccrw1500: Number(getColumnValue(row, ccrw1500Idx, 0)),
                ccrw1800: Number(getColumnValue(row, ccrw1800Idx, 0)),
                ccrw2000: Number(getColumnValue(row, ccrw2000Idx, 0)),
                ccrw2250: Number(getColumnValue(row, ccrw2250Idx, 0)),
                ccrw2500: Number(getColumnValue(row, ccrw2500Idx, 0)),
                ccrw2750: Number(getColumnValue(row, ccrw2750Idx, 0)),
                ccrw3000: Number(getColumnValue(row, ccrw3000Idx, 0)),
                ccrw3250: Number(getColumnValue(row, ccrw3250Idx, 0)),
                ccrw3500: Number(getColumnValue(row, ccrw3500Idx, 0)),
                drw450: Number(getColumnValue(row, drw450Idx, 0)),
                drw600: Number(getColumnValue(row, drw600Idx, 0)),
                drw750: Number(getColumnValue(row, drw750Idx, 0)),
                drw900: Number(getColumnValue(row, drw900Idx, 0)),
                drw1050: Number(getColumnValue(row, drw1050Idx, 0)),
                drw1200: Number(getColumnValue(row, drw1200Idx, 0)),
                drw1500: Number(getColumnValue(row, drw1500Idx, 0)),
                drw1800: Number(getColumnValue(row, drw1800Idx, 0)),
                drw2000: Number(getColumnValue(row, drw2000Idx, 0)),
                drw2250: Number(getColumnValue(row, drw2250Idx, 0)),
                drw2500: Number(getColumnValue(row, drw2500Idx, 0)),
                drw2750: Number(getColumnValue(row, drw2750Idx, 0)),
                drw3000: Number(getColumnValue(row, drw3000Idx, 0)),
              };
            })
            .filter((row) => {
              const plotNoStr = String(row.plotNo || "").trim();
              const hasPlotNo =
                plotNoStr !== "" &&
                plotNoStr !== "0" &&
                plotNoStr !== "null" &&
                plotNoStr !== "undefined";
              const housetypeStr = String(row.housetype || "").trim();
              const hasValidHouseType =
                housetypeStr !== "" &&
                !housetypeStr.toLowerCase().includes("total") &&
                !housetypeStr.toLowerCase().includes("sum") &&
                isNaN(Number(housetypeStr)) && // Not purely numeric
                housetypeStr.match(/[a-zA-Z]/); // Contains letters
              return hasPlotNo && hasValidHouseType;
            });

          if (allImportedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          // Count found columns for summary
          const foundColumns = [
            plotNoIdx,
            housetypeIdx,
            screenWallsEOIdx,
            screenWallsSofIdx,
            screenWallsLogIdx,
            screenWallsGravelBoardIdx,
            screenWallsAdditionalExcavationIdx,
            screenWallsAdditionalConcreteIdx,
            grw450Idx,
            grw600Idx,
            grw750Idx,
            grw900Idx,
            grw1050Idx,
            grw1200Idx,
            grw1500Idx,
            grw1800Idx,
            grw2000Idx,
            ccrw1500Idx,
            ccrw1800Idx,
            ccrw2000Idx,
            ccrw2250Idx,
            ccrw2500Idx,
            ccrw2750Idx,
            ccrw3000Idx,
            ccrw3250Idx,
            ccrw3500Idx,
            drw450Idx,
            drw600Idx,
            drw750Idx,
            drw900Idx,
            drw1050Idx,
            drw1200Idx,
            drw1500Idx,
            drw1800Idx,
            drw2000Idx,
            drw2250Idx,
            drw2500Idx,
            drw2750Idx,
            drw3000Idx,
          ].filter((idx) => idx !== -1).length;

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            retainingWallsTable: allImportedRows,
          });
          alert(
            `Successfully imported ${allImportedRows.length} rows. Found ${foundColumns} matching columns out of 39 possible columns.`,
          );
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.retainingWallsTable) {
      setData(tender.groundworks.retainingWallsTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.retainingWallsTable]);

  const createEmptyRow = (key?: string): RetainingWallsRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",

    // Screen Walls section
    screenWalls: 0,
    screenWallsEO: 0,
    screenWallsSof: 0,
    screenWallsLog: 0,
    screenWallsGravelBoard: 0,
    screenWallsAdditionalExcavation: 0,
    screenWallsAdditionalConcrete: 0,

    // Garden Retaining Wall sections
    grw450: 0,
    grw600: 0,
    grw750: 0,
    grw900: 0,
    grw1050: 0,
    grw1200: 0,
    grw1500: 0,
    grw1800: 0,
    grw2000: 0,

    // Concrete Core Walls
    ccrw1500: 0,
    ccrw1800: 0,
    ccrw2000: 0,
    ccrw2250: 0,
    ccrw2500: 0,
    ccrw2750: 0,
    ccrw3000: 0,
    ccrw3250: 0,
    ccrw3500: 0,

    // Drive Retaining Walls
    drw450: 0,
    drw600: 0,
    drw750: 0,
    drw900: 0,
    drw1050: 0,
    drw1200: 0,
    drw1500: 0,
    drw1800: 0,
    drw2000: 0,
    drw2250: 0,
    drw2500: 0,
    drw2750: 0,
    drw3000: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof RetainingWallsRow,
    value: string | number,
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        retainingWallsTable: newData,
      });
      return newData;
    });
  };

  const getScrollConfig = (columns: { width?: number | string }[]) => {
    const totalWidth = columns.reduce((sum, col) => sum + (Number(col.width) || 120), 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      retainingWallsTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedRetainingWallKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Ret & Screen Walls Bill Summary — see CutFills.tsx's
      // identical fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        retainingWallsTable: newData,
        ...(newData.length === 0 && { retainingWallsExtraItems: {} }),
      });
      setSelectedRetainingWallKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  // Calculate column totals
  const calculateColumnTotals = () => {
    return data.reduce(
      (totals, row) => {
        return {
          screenWalls: totals.screenWalls + (row.screenWalls || 0),
          screenWallsEO: totals.screenWallsEO + (row.screenWallsEO || 0),
          screenWallsSof: totals.screenWallsSof + (row.screenWallsSof || 0),
          screenWallsLog: totals.screenWallsLog + (row.screenWallsLog || 0),
          screenWallsGravelBoard:
            totals.screenWallsGravelBoard + (row.screenWallsGravelBoard || 0),
          screenWallsAdditionalExcavation:
            totals.screenWallsAdditionalExcavation + (row.screenWallsAdditionalExcavation || 0),
          screenWallsAdditionalConcrete:
            totals.screenWallsAdditionalConcrete + (row.screenWallsAdditionalConcrete || 0),
          grw450: totals.grw450 + (row.grw450 || 0),
          grw600: totals.grw600 + (row.grw600 || 0),
          grw750: totals.grw750 + (row.grw750 || 0),
          grw900: totals.grw900 + (row.grw900 || 0),
          grw1050: totals.grw1050 + (row.grw1050 || 0),
          grw1200: totals.grw1200 + (row.grw1200 || 0),
          grw1500: totals.grw1500 + (row.grw1500 || 0),
          grw1800: totals.grw1800 + (row.grw1800 || 0),
          grw2000: totals.grw2000 + (row.grw2000 || 0),
          drw450: totals.drw450 + (row.drw450 || 0),
          drw600: totals.drw600 + (row.drw600 || 0),
          drw750: totals.drw750 + (row.drw750 || 0),
          drw900: totals.drw900 + (row.drw900 || 0),
          drw1050: totals.drw1050 + (row.drw1050 || 0),
          drw1200: totals.drw1200 + (row.drw1200 || 0),
          drw1500: totals.drw1500 + (row.drw1500 || 0),
          drw1800: totals.drw1800 + (row.drw1800 || 0),
          drw2000: totals.drw2000 + (row.drw2000 || 0),
          ccrw1500: totals.ccrw1500 + (row.ccrw1500 || 0),
          ccrw1800: totals.ccrw1800 + (row.ccrw1800 || 0),
          ccrw2000: totals.ccrw2000 + (row.ccrw2000 || 0),
          ccrw2250: totals.ccrw2250 + (row.ccrw2250 || 0),
          ccrw2500: totals.ccrw2500 + (row.ccrw2500 || 0),
          ccrw2750: totals.ccrw2750 + (row.ccrw2750 || 0),
          ccrw3000: totals.ccrw3000 + (row.ccrw3000 || 0),
          ccrw3250: totals.ccrw3250 + (row.ccrw3250 || 0),
          ccrw3500: totals.ccrw3500 + (row.ccrw3500 || 0),
        };
      },
      {
        screenWalls: 0,
        screenWallsEO: 0,
        screenWallsSof: 0,
        screenWallsLog: 0,
        screenWallsGravelBoard: 0,
        screenWallsAdditionalExcavation: 0,
        screenWallsAdditionalConcrete: 0,
        grw450: 0,
        grw600: 0,
        grw750: 0,
        grw900: 0,
        grw1050: 0,
        grw1200: 0,
        grw1500: 0,
        grw1800: 0,
        grw2000: 0,
        drw450: 0,
        drw600: 0,
        drw750: 0,
        drw900: 0,
        drw1050: 0,
        drw1200: 0,
        drw1500: 0,
        drw1800: 0,
        drw2000: 0,
        ccrw1500: 0,
        ccrw1800: 0,
        ccrw2000: 0,
        ccrw2250: 0,
        ccrw2500: 0,
        ccrw2750: 0,
        ccrw3000: 0,
        ccrw3250: 0,
        ccrw3500: 0,
      },
    );
  };

  const columnTotals = calculateColumnTotals();

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const { version } = await forceSaveBothBoqEndpoints(
        tender,
        "Save and continue from Retaining Walls",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      // Match the on-change save path in WallsSpecificationContainer:
      // always push current state to the walls API. The previous
      // `version >= 2 → copyWallsBuildUpFromPreviousVersion` branch was
      // overwriting the user's just-edited build-up with v(n-1)'s data on
      // every Save & Continue from v2+ — so the dedicated walls API ended
      // up holding the previous version's specs instead of the edits the
      // debounced on-change save had just written.
      await saveRetainingWallsBuildUp({ tender, itemLibraryData, ohpPercent: groundworksTotals.ohpPercent, prelimsPercent: groundworksTotals.preliminariesPercent, versionOverride: version });

      // Direct API POST above doesn't go through the mutation hook, so the
      // React Query cache for the bill summary's GET stays "fresh" and serves
      // pre-save data. Invalidate it so Groundworks Review's Ret & Screen
      // Walls bill summary refetches the latest specs on next mount.
      await queryClient.invalidateQueries({
        queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(tender.projectName, version),
      });

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/geo-tober-wall"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const { version } = await forceSaveBothBoqEndpoints(
        tender,
        "Save and exit from Retaining Walls",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      // Match the on-change save path — always save current state to the
      // walls API. See Save & Continue handler above for the rationale.
      await saveRetainingWallsBuildUp({ tender, itemLibraryData, ohpPercent: groundworksTotals.ohpPercent, prelimsPercent: groundworksTotals.preliminariesPercent, versionOverride: version });

      // Invalidate bill-summary cache for the same reason as Save & Continue
      // (direct API POST bypasses the mutation hook's onSuccess invalidation).
      await queryClient.invalidateQueries({
        queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(tender.projectName, version),
      });

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const baseColumns = [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: any) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) => {
            if (isDuplicatePlotNo(data, record.key, newValue)) {
              message.error(`Plot No "${newValue}" is already in use.`);
              return;
            }
            handleInputChange(record.key, "plotNo", newValue);
          }}
          placeholder="Plot No"
        />
      ),
    },
    {
      title: "Housetype",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: any) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(record.key, "housetype", e.target.value)
          }
          placeholder="Housetype"
        />
      ),
    },
  ];

  const retainingWallColumns = [
    ...baseColumns,
    {
      title: formatColumnTitle(`Screen Walls - ${screenWallsSuffix}`),
      dataIndex: "screenWalls",
      key: "screenWalls",
      width: 110,
      render: (value: number, record: any) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "screenWalls", newValue)
          }
          decimalPlaces={3}
          placeholder="0.000"
        />
      ),
    },
    {
      title: formatColumnTitle(`EO Pillar - ${eoPillarSuffix} (Nr)`),
      dataIndex: "screenWallsEO",
      key: "screenWallsEO",
      width: 110,
      render: (value: number, record: any) => {
        /* totals removed */ if (false) {
          return (
            <span className="font-bold text-red-600">{value.toFixed(2)}</span>
          );
        }
        return (
          <NumberInput
            value={value}
            onChange={(newValue) =>
              handleInputChange(record.key, "screenWallsEO", newValue)
            }
            decimalPlaces={3}
            placeholder="0.000"
          />
        );
      },
    },
    {
      title: formatColumnTitle("SoE 450mm (m)"),
      dataIndex: "screenWallsSof",
      key: "screenWallsSof",
      width: 110,
      render: (value: number, record: any) => {
        /* totals removed */ if (false) {
          return (
            <span className="font-bold text-red-600">{value.toFixed(2)}</span>
          );
        }
        return (
          <NumberInput
            value={value}
            onChange={(newValue) =>
              handleInputChange(record.key, "screenWallsSof", newValue)
            }
            decimalPlaces={3}
            placeholder="0.000"
          />
        );
      },
    },
    {
      title: formatColumnTitle("Log 1200mm (m)"),
      dataIndex: "screenWallsLog",
      key: "screenWallsLog",
      width: 110,
      render: (value: number, record: any) => {
        /* totals removed */ if (false) {
          return (
            <span className="font-bold text-red-600">{value.toFixed(2)}</span>
          );
        }
        return (
          <NumberInput
            value={value}
            onChange={(newValue) =>
              handleInputChange(record.key, "screenWallsLog", newValue)
            }
            decimalPlaces={3}
            placeholder="0.000"
          />
        );
      },
    },
    {
      title: formatColumnTitle("Gravel board (m)"),
      dataIndex: "screenWallsGravelBoard",
      key: "screenWallsGravelBoard",
      width: 110,
      render: (value: number, record: any) => {
        /* totals removed */ if (false) {
          return (
            <span className="font-bold text-red-600">{value.toFixed(2)}</span>
          );
        }
        return (
          <NumberInput
            value={value}
            onChange={(newValue) =>
              handleInputChange(record.key, "screenWallsGravelBoard", newValue)
            }
            decimalPlaces={3}
            placeholder="0.000"
          />
        );
      },
    },
    {
      title: formatColumnTitle("Additional Excavation (m³)"),
      dataIndex: "screenWallsAdditionalExcavation",
      key: "screenWallsAdditionalExcavation",
      width: 110,
      render: (value: number, record: any) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "screenWallsAdditionalExcavation", newValue)
          }
          decimalPlaces={3}
          placeholder="0.000"
        />
      ),
    },
    {
      title: formatColumnTitle("Additional Concrete (m³)"),
      dataIndex: "screenWallsAdditionalConcrete",
      key: "screenWallsAdditionalConcrete",
      width: 110,
      render: (value: number, record: any) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "screenWallsAdditionalConcrete", newValue)
          }
          decimalPlaces={3}
          placeholder="0.000"
        />
      ),
    },
    {
      title: (
        <div className="text-center font-bold text-green-700 bg-green-100 p-2">
          Garden Retaining Wall
        </div>
      ),
      key: "gardenRetainingGroup",
      children: [
        {
          title: formatColumnTitle("450 GRW450 (m)"),
          dataIndex: "grw450",
          key: "grw450",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw450", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("600 GRW600 (m)"),
          dataIndex: "grw600",
          key: "grw600",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw600", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("750 GRW750 (m)"),
          dataIndex: "grw750",
          key: "grw750",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw750", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("900 GRW900 (m)"),
          dataIndex: "grw900",
          key: "grw900",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw900", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1050 GRW1050 (m)"),
          dataIndex: "grw1050",
          key: "grw1050",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw1050", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1200 GRW1200 (m)"),
          dataIndex: "grw1200",
          key: "grw1200",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw1200", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1500 GRW1500 (m)"),
          dataIndex: "grw1500",
          key: "grw1500",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw1500", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1800 GRW1800 (m)"),
          dataIndex: "grw1800",
          key: "grw1800",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw1800", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("2000 GRW2000 (m)"),
          dataIndex: "grw2000",
          key: "grw2000",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "grw2000", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
      ],
    },
    {
      title: (
        <div className="text-center font-bold text-orange-700 bg-orange-100 p-2">
          Drive Retaining Walls
        </div>
      ),
      key: "driveRetainingWallsGroup",
      children: [
        {
          title: formatColumnTitle("450 DRW450 (m)"),
          dataIndex: "drw450",
          key: "drw450",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw450", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("600 DRW600 (m)"),
          dataIndex: "drw600",
          key: "drw600",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw600", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("750 DRW750 (m)"),
          dataIndex: "drw750",
          key: "drw750",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw750", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("900 DRW900 (m)"),
          dataIndex: "drw900",
          key: "drw900",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw900", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1050 DRW1050 (m)"),
          dataIndex: "drw1050",
          key: "drw1050",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw1050", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1200 DRW1200 (m)"),
          dataIndex: "drw1200",
          key: "drw1200",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw1200", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1500 DRW1500 (m)"),
          dataIndex: "drw1500",
          key: "drw1500",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw1500", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("1800 DRW1800 (m)"),
          dataIndex: "drw1800",
          key: "drw1800",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw1800", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        {
          title: formatColumnTitle("2000 DRW2000 (m)"),
          dataIndex: "drw2000",
          key: "drw2000",
          width: 110,
          render: (value: number, record: any) => {
            /* totals removed */ if (false) {
              return (
                <span className="font-bold text-red-600">
                  {value.toFixed(2)}
                </span>
              );
            }
            return (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "drw2000", newValue)
                }
                decimalPlaces={3}
                placeholder="0.000"
              />
            );
          },
        },
        // {
        //   title: formatColumnTitle("2250 DRW2250 (m)"),
        //   dataIndex: "drw2250",
        //   key: "drw2250",
        //   width: 110,
        //   render: (value: number, record: any) => {
        //     /* totals removed */ if (false) {
        //       return (
        //         <span className="font-bold text-red-600">
        //           {value.toFixed(2)}
        //         </span>
        //       );
        //     }
        //     return (
        //       <NumberInput
        //         value={value}
        //         onChange={(newValue) =>
        //           handleInputChange(record.key, "drw2250", newValue)
        //         }
        //         decimalPlaces={3}
        //         placeholder="0.000"
        //       />
        //     );
        //   },
        // },
        // {
        //   title: formatColumnTitle("2500 DRW2500 (m)"),
        //   dataIndex: "drw2500",
        //   key: "drw2500",
        //   width: 110,
        //   render: (value: number, record: any) => {
        //     /* totals removed */ if (false) {
        //       return (
        //         <span className="font-bold text-red-600">
        //           {value.toFixed(2)}
        //         </span>
        //       );
        //     }
        //     return (
        //       <NumberInput
        //         value={value}
        //         onChange={(newValue) =>
        //           handleInputChange(record.key, "drw2500", newValue)
        //         }
        //         decimalPlaces={3}
        //         placeholder="0.000"
        //       />
        //     );
        //   },
        // },
        // {
        //   title: formatColumnTitle("2750 DRW2750 (m)"),
        //   dataIndex: "drw2750",
        //   key: "drw2750",
        //   width: 110,
        //   render: (value: number, record: any) => {
        //     /* totals removed */ if (false) {
        //       return (
        //         <span className="font-bold text-red-600">
        //           {value.toFixed(2)}
        //         </span>
        //       );
        //     }
        //     return (
        //       <NumberInput
        //         value={value}
        //         onChange={(newValue) =>
        //           handleInputChange(record.key, "drw2750", newValue)
        //         }
        //         decimalPlaces={3}
        //         placeholder="0.000"
        //       />
        //     );
        //   },
        // },
        // {
        //   title: formatColumnTitle("3000 DRW3000 (m)"),
        //   dataIndex: "drw3000",
        //   key: "drw3000",
        //   width: 110,
        //   render: (value: number, record: any) => {
        //     /* totals removed */ if (false) {
        //       return (
        //         <span className="font-bold text-red-600">
        //           {value.toFixed(2)}
        //         </span>
        //       );
        //     }
        //     return (
        //       <NumberInput
        //         value={value}
        //         onChange={(newValue) =>
        //           handleInputChange(record.key, "drw3000", newValue)
        //         }
        //         decimalPlaces={3}
        //         placeholder="0.000"
        //       />
        //     );
        //   },
        // },
      ],
    },
    {
      title: (
        <div className="text-center font-bold text-purple-700 bg-purple-100 p-2">
          Concrete Core Walls
        </div>
      ),
      key: "concreteCorewallsGroup",
      children: [
        {
          title: formatColumnTitle("1500 CCRW1500 (m)"),
          dataIndex: "ccrw1500",
          key: "ccrw1500",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw1500", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("1800 CCRW1800 (m)"),
          dataIndex: "ccrw1800",
          key: "ccrw1800",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw1800", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("2000 CCRW2000 (m)"),
          dataIndex: "ccrw2000",
          key: "ccrw2000",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw2000", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("2250 CCRW2250 (m)"),
          dataIndex: "ccrw2250",
          key: "ccrw2250",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw2250", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("2500 CCRW2500 (m)"),
          dataIndex: "ccrw2500",
          key: "ccrw2500",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw2500", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("2750 CCRW2750 (m)"),
          dataIndex: "ccrw2750",
          key: "ccrw2750",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw2750", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("3000 CCRW3000 (m)"),
          dataIndex: "ccrw3000",
          key: "ccrw3000",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw3000", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("3250 CCRW3250 (m)"),
          dataIndex: "ccrw3250",
          key: "ccrw3250",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw3250", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
        {
          title: formatColumnTitle("3500 CCRW3500 (m)"),
          dataIndex: "ccrw3500",
          key: "ccrw3500",
          width: 110,
          render: (value: number, record: any) => (
            <NumberInput
              value={value}
              onChange={(newValue) => handleInputChange(record.key, "ccrw3500", newValue)}
              decimalPlaces={3}
              placeholder="0.000"
            />
          ),
        },
      ],
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: any) => {
        /* totals removed */ if (false) {
          return null;
        }
        return (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        );
      },
    },
  ];

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm retaining-walls-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title and Tabs */}
        <div
          className="retaining-walls-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Ret & Screen Walls
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Total Quantities - Garden, Drive & Concrete Core Walls
                  construction specifications.
                </Text>
              </div>
              {activeTab === "quantities" && (
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => {
                    setTempScreenWallsLabel(screenWallsLabel);
                    setTempEOPillarLabel(eoPillarLabel);
                    setScreenWallsConfigOpen(true);
                  }}
                  type="default"
                  style={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                    marginTop: "18px",
                  }}
                  className="configure-headers-btn"
                >
                  Configure Headers
                </Button>
              )}
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="retaining-walls-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {/* Left side - Tab buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                }}
              >
                <Button
                  type={activeTab === "quantities" ? "primary" : "default"}
                  onClick={() => setActiveTab("quantities")}
                  className={
                    activeTab === "quantities"
                      ? "active-tab-button"
                      : "inactive-tab-button"
                  }
                  style={{
                    backgroundColor:
                      activeTab === "quantities" ? COLORS.primary : "#E1E6EF",
                    borderColor:
                      activeTab === "quantities" ? COLORS.primary : "#E1E6EF",
                    color: activeTab === "quantities" ? "#fff" : "#6B7280",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    height: "38px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    borderBottom:
                      activeTab === "quantities" ? "none" : "1px solid #E1E6EF",
                    position: "relative",
                    zIndex: activeTab === "quantities" ? 2 : 1,
                  }}
                >
                  Ret & Screen Take off
                </Button>
                <Button
                  type={activeTab === "buildup" ? "primary" : "default"}
                  onClick={() => setActiveTab("buildup")}
                  className={
                    activeTab === "buildup"
                      ? "active-tab-button"
                      : "inactive-tab-button"
                  }
                  style={{
                    backgroundColor:
                      activeTab === "buildup" ? COLORS.primary : "#E1E6EF",
                    borderColor:
                      activeTab === "buildup" ? COLORS.primary : "#E1E6EF",
                    color: activeTab === "buildup" ? "#fff" : "#6B7280",
                    fontWeight: 500,
                    borderRadius: "6px 6px 0 0",
                    height: "38px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    borderBottom:
                      activeTab === "buildup" ? "none" : "1px solid #E1E6EF",
                    position: "relative",
                    zIndex: activeTab === "buildup" ? 2 : 1,
                  }}
                >
                  Walls Section Build Up
                </Button>
              </div>

              {/* Right side - Configure + Delete Selected (Ret & Screen Take off tab only) */}
              {activeTab === "quantities" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  {selectedRetainingWallKeys.length > 0 && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={deleteSelectedRows}
                      style={{ height: "32px", borderRadius: "6px", fontWeight: 500 }}
                    >
                      Delete Selected ({selectedRetainingWallKeys.length})
                    </Button>
                  )}
                </div>
              )}

              {/* Right side - Wall Type select + Configure button (Walls Section Build Up tab only) */}
              {activeTab === "buildup" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  <Text strong>Wall Type:</Text>
                  <Select
                    value={buildUpSelectedWallType}
                    onChange={(value: string) => setBuildUpSelectedWallType(value)}
                    style={{ width: 250 }}
                    className="supplier-select"
                    options={buildUpWallTypeOptions}
                  />
                  <Button
                    icon={<SettingOutlined />}
                    onClick={() => {
                      setBuildUpTempFacingLabel(buildUpFacingLabel);
                      setBuildUpConfigOpen(true);
                    }}
                    type="default"
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      flexShrink: 0,
                    }}
                    className="configure-headers-btn"
                  >
                    Configure
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 retaining-walls-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Quantities Table */}
          {activeTab === "quantities" && (
            <div>
              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={data}
                columns={addColumnCodes(retainingWallColumns, RETAINING_WALLS_REFERENCES, {
                  screenWalls: resolveScreenWallsCode(screenWallsLabel),
                  screenWallsEO: resolveEOPillarCode(eoPillarLabel),
                })}
                pagination={false}
                scroll={getScrollConfig(retainingWallColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName(
                        "retaining-walls-sticky-header",
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="retaining-walls-table"
                rowSelection={{
                  selectedRowKeys: selectedRetainingWallKeys,
                  onChange: setSelectedRetainingWallKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                summary={() => {
                  if (data.length === 0) return null;
                  const fields = [
                    "screenWalls",
                    "screenWallsEO",
                    "screenWallsSof",
                    "screenWallsLog",
                    "screenWallsGravelBoard",
                    "screenWallsAdditionalExcavation",
                    "screenWallsAdditionalConcrete",
                    "grw450",
                    "grw600",
                    "grw750",
                    "grw900",
                    "grw1050",
                    "grw1200",
                    "grw1500",
                    "grw1800",
                    "grw2000",
                    "drw450",
                    "drw600",
                    "drw750",
                    "drw900",
                    "drw1050",
                    "drw1200",
                    "drw1500",
                    "drw1800",
                    "drw2000",
                    "ccrw1500",
                    "ccrw1800",
                    "ccrw2000",
                    "ccrw2250",
                    "ccrw2500",
                    "ccrw2750",
                    "ccrw3000",
                    "ccrw3250",
                    "ccrw3500",
                  ];
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        {fields.map((field, idx) => (
                          <Table.Summary.Cell
                            key={field}
                            index={idx + 3}
                            align="right"
                          >
                            <span
                              style={{
                                color: "#000",
                                textAlign: "right",
                                display: "block",
                                paddingRight: "8px",
                              }}
                            >
                              {(columnTotals[field] || 0).toFixed(3)}
                            </span>
                          </Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell
                          index={fields.length + 3}
                        ></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}

              {/* Add New Row Button */}
              <div style={{ marginTop: "16px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>

              <Modal
                title="Configure Headers"
                open={screenWallsConfigOpen}
                onCancel={() => setScreenWallsConfigOpen(false)}
                onOk={async () => {
                  const updatedGroundworks = {
                    ...tender.groundworks,
                    wallsBuildUpConfig: {
                      ...tender.groundworks?.wallsBuildUpConfig,
                      screenWallsLabel: tempScreenWallsLabel,
                      eoPillarLabel: tempEOPillarLabel,
                    },
                  };
                  updateField("groundworks", updatedGroundworks);
                  setScreenWallsConfigOpen(false);

                  // Persist to /retaining-walls-buildup immediately (like the
                  // Facing Brickwork Configure modal) so the choice is durable
                  // on refresh/navigation without waiting for Save & Continue —
                  // Plot Matrix and the Ret & Screen Walls Bill Summary read
                  // `wallsBuildUpConfig` from that persisted API response, not
                  // just from this page's local/localStorage tender state.
                  try {
                    await saveRetainingWallsBuildUp({
                      tender: { ...tender, groundworks: updatedGroundworks },
                      itemLibraryData,
                      ohpPercent: groundworksTotals.ohpPercent,
                      prelimsPercent: groundworksTotals.preliminariesPercent,
                    });
                    const boqVersionRaw = localStorage.getItem("currentBoqVersion");
                    const currentVersion = boqVersionRaw ? getNumericBoqVersion(JSON.parse(boqVersionRaw)?.version ?? 1) : 1;
                    await queryClient.invalidateQueries({
                      queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(tender.projectName, currentVersion),
                    });
                  } catch (err) {
                    console.error("Failed to persist Screen Walls / EO Pillar config:", err);
                  }
                }}
                okText="Save"
              >
                <Text strong className="block mb-2">
                  Screen Walls
                </Text>
                <Select
                  value={tempScreenWallsLabel}
                  onChange={(value: string) => setTempScreenWallsLabel(value)}
                  style={{ width: "100%", marginBottom: 16 }}
                  options={[
                    { value: SCREEN_WALLS_FREE_ISSUE_LABEL, label: SCREEN_WALLS_FREE_ISSUE_LABEL },
                    { value: SCREEN_WALLS_M_J_SUPPLY_LABEL, label: SCREEN_WALLS_M_J_SUPPLY_LABEL },
                  ]}
                />
                <Text strong className="block mb-2">
                  EO Pillar
                </Text>
                <Select
                  value={tempEOPillarLabel}
                  onChange={(value: string) => setTempEOPillarLabel(value)}
                  style={{ width: "100%" }}
                  options={[
                    { value: EO_PILLAR_FREE_ISSUE_LABEL, label: EO_PILLAR_FREE_ISSUE_LABEL },
                    { value: EO_PILLAR_M_J_SUPPLY_LABEL, label: EO_PILLAR_M_J_SUPPLY_LABEL },
                  ]}
                />
              </Modal>
            </div>
          )}

          {/* Always mounted so registry listener stays active */}
          <div style={{ paddingTop: "16px", display: activeTab === "buildup" ? "block" : "none" }}>
            <WallsSpecificationContainer
              selectedWallType={buildUpSelectedWallType}
              onSelectedWallTypeChange={setBuildUpSelectedWallType}
              configModalVisible={buildUpConfigOpen}
              setConfigModalVisible={setBuildUpConfigOpen}
              tempFacingBrickworkLabel={buildUpTempFacingLabel}
              setTempFacingBrickworkLabel={setBuildUpTempFacingLabel}
              onWallTypeOptionsChange={setBuildUpWallTypeOptions}
            />
          </div>

        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden Excel Import/Export Component */}
      <div style={{ display: "none" }}>
        <ExcelImportExport
          onExport={handleExportExcel}
          onImport={handleImportExcel}
          onDownloadTemplate={handleDownloadTemplate}
          importLoading={importLoading}
          importModalVisible={importModalVisible}
          setImportModalVisible={setImportModalVisible}
          hasData={data.length > 0}
        />
      </div>

      <style>{`
        .retaining-walls-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .retaining-walls-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .retaining-walls-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .retaining-walls-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .retaining-walls-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .retaining-walls-card .ant-card-body {
          overflow: visible !important;
        }

        .retaining-walls-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .retaining-walls-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
          margin-bottom: -1px;
        }

        .inactive-tab-button {
          border-bottom: 1px solid #d9d9d9 !important;
        }

        .retaining-walls-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .retaining-walls-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .retaining-walls-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .retaining-walls-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: 1px solid #e5e7eb !important;
        }

        .retaining-walls-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .retaining-walls-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .retaining-walls-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        .retaining-walls-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          z-index: 100 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .retaining-walls-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .retaining-walls-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .retaining-walls-table .ant-table-thead > tr > th.ant-table-selection-column,
        .retaining-walls-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .retaining-walls-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .retaining-walls-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .retaining-walls-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row — keep the normal/hover backgrounds */
        .retaining-walls-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .retaining-walls-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        .retaining-walls-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .retaining-walls-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude first 2 columns: Plot No, Housetype) */
        .retaining-walls-table td:not(:nth-child(1)):not(:nth-child(2)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .retaining-walls-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .retaining-walls-table .ant-input-number {
          width: 100%;
        }

        .retaining-walls-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .retaining-walls-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .retaining-walls-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .retaining-walls-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .retaining-walls-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* RET & Screen Walls Quants Modal */}
      <Modal
        title="RET & Screen Walls Quants - Summary"
        open={isQuantsModalOpen}
        onCancel={() => setIsQuantsModalOpen(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        zIndex={1050}
      >
        <RetScreenWallsQuants />
      </Modal>
    </MainLayout>
  );
};

export default RetainingWalls;
